"use client";

import { useRef, type JSX } from "react";
import NextScript from "next/script";
import { type Field } from "@sitecore-content-sdk/nextjs";
import { ComponentProps } from "lib/component-props";

type Strategy =
  | "afterInteractive"
  | "lazyOnload"
  | "beforeInteractive"
  | "worker";

export type ScriptFields = {
  Value?: Field<string>;
  Attributes?: Field<string>;
};

export type ScriptProps = ComponentProps & {
  fields?: ScriptFields;
};

const parseKeyValuePairs = (input?: string): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!input) return result;

  for (const pair of input.split("&")) {
    const [key, value] = pair.split("=");
    if (key && value) {
      result[decodeURIComponent(key)] = decodeURIComponent(value);
    }
  }
  return result;
};

const convertAttributesToString = (
  attributes: Record<string, string>,
): string =>
  Object.keys(attributes)
    .map((key) => `${key}="${attributes[key]}"`)
    .join(" ");

export const Default = (props: ScriptProps): JSX.Element => {
  console.log("---->[Script] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  const containerRef = useRef<HTMLDivElement>(null);

  const strategy = (params?.Strategy || "afterInteractive") as Strategy;
  const renderInline = params?.RenderAsInlineScript === "1";
  const renderInEditingMode = params?.RenderInEditingMode === "1";
  const attributes = parseKeyValuePairs(fields?.Attributes?.value);
  const id = attributes.id || params?.RenderingIdentifier;

  let rawScript = fields?.Value?.value?.trim();
  if (!renderInline && rawScript) {
    attributes.src = rawScript;
    rawScript = undefined;
  }

  // Bugcrowd's external submission form is appended to <body> by their
  // injected script; this callback re-parents it into our container so we
  // can position/style it predictably.
  const moveScript = () => {
    const iframe = document.getElementById("bugcrowd-external-submission-form");
    if (iframe && containerRef.current) {
      containerRef.current.appendChild(iframe);
    }
  };

  if (!rawScript && !attributes.src) {
    return (
      <div className="component script">
        <div className="component-content">
          <span className="is-empty-hint">
            <strong>Script:</strong> component is missing datasource or Value
            field is empty
          </span>
        </div>
      </div>
    );
  }

  if (isPageEditing) {
    if (renderInEditingMode) {
      return (
        <NextScript
          id={id || undefined}
          strategy={strategy}
          defer
          {...attributes}
        >
          {rawScript}
        </NextScript>
      );
    }

    return rawScript ? (
      <pre>
        {`<script ${convertAttributesToString(attributes)} defer={true}>\n${rawScript}\n</script>`}
      </pre>
    ) : (
      <pre>
        {`<script ${convertAttributesToString(attributes)} defer={true}></script>`}
      </pre>
    );
  }

  const isBugcrowd = !!attributes["data-bugcrowd-program"];

  if (isBugcrowd) {
    return (
      <div ref={containerRef} id="script-container">
        <NextScript
          id={id || undefined}
          strategy={strategy}
          defer
          onLoad={moveScript}
          {...attributes}
        >
          {rawScript}
        </NextScript>
      </div>
    );
  }

  return (
    <NextScript id={id || undefined} strategy={strategy} defer {...attributes}>
      {rawScript}
    </NextScript>
  );
};
